import type { EchoJourneyNode } from "../types";

type Document = Record<string, unknown>;
function document(value: unknown): Document | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Document)
    : null;
}

/** Event observations are neither final asset verification nor billing receipts. */
export function buildRepromptActualEvidence(
  nodes: EchoJourneyNode[],
  workloadId: string,
  authorizedMotionScenes: unknown,
) {
  const scenes = new Set<number>();
  const speech = new Set<number>();
  const music = new Set<string>();
  const attempts = new Map<string, Document>();
  for (const node of nodes) {
    const raw = document(node.raw_payload);
    const payload = document(raw?.payload) ?? raw;
    if (!payload) continue;
    const lifecycle = document(payload.lifecycle) ?? payload;
    const owner = lifecycle.workload_id ?? node.workload_id ?? node.session_id;
    // Legacy unbound observations cannot establish reuse for this child.
    if (owner !== workloadId) continue;
    if (
      node.type === "scene.asset_reused" &&
      typeof payload.scene_id === "number"
    ) {
      scenes.add(payload.scene_id);
    }
    if (
      node.type === "music.asset.reused" &&
      typeof payload.s3_key === "string"
    ) {
      music.add(payload.s3_key);
    }
    if (node.type === "voice.assets_reused" && Array.isArray(payload.indices)) {
      for (const index of payload.indices) {
        if (typeof index === "number") speech.add(index);
      }
    }
    if (
      (node.type === "i2v_attempt" ||
        node.type.startsWith("scene.i2v.attempt_")) &&
      typeof lifecycle.audit_id === "string" &&
      lifecycle.audit_id
    ) {
      const previous = attempts.get(lifecycle.audit_id);
      // Do not let a late submission event overwrite a terminal receipt.
      if (!previous?.terminal || lifecycle.terminal) {
        attempts.set(lifecycle.audit_id, lifecycle);
      }
    }
  }
  const generatedAttempts = [...attempts.values()].filter(
    (attempt) => !["reused", "skipped"].includes(String(attempt.status)),
  );
  const permitted = Array.isArray(authorizedMotionScenes)
    ? new Set(authorizedMotionScenes)
    : null;
  return {
    reusedSceneIds: [...scenes].sort((a, b) => a - b),
    reusedSpeechIndices: [...speech].sort((a, b) => a - b),
    reusedMusicAssets: music.size,
    motionAttemptCount: generatedAttempts.length,
    successfulMotionAttempts: generatedAttempts.filter(
      (a) => a.status === "succeeded",
    ).length,
    failedMotionAttempts: generatedAttempts.filter((a) => a.status === "failed")
      .length,
    outsidePlanSceneIds: permitted
      ? [
          ...new Set(
            generatedAttempts
              .map((a) => a.scene_id)
              .filter(
                (id): id is number =>
                  typeof id === "number" && !permitted.has(id),
              ),
          ),
        ].sort((a, b) => a - b)
      : null,
  };
}
